import { useEffect, useRef, useState } from "react";
import { Link, useRoute } from "wouter";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AmountField, parseAmount } from "@/components/amount-field";
import { LoadFields, buildFieldState, collectFieldPatch } from "@/components/load-fields";
import { describeError, loadFileUrl, useLoad, usePatchLoad, type LoadPatchPayload } from "@/lib/api";
import { formatLoadDate } from "@/lib/format";

// Read view of one load, with its captured ticket image and an editable
// amount. Unlike review, amount is not required here — an already-saved
// load may legitimately be revisited without changing the pay — but it
// still has to be VALID to save.
export function Detail() {
  const [, params] = useRoute<{ id: string }>("/l/:id");
  const id = params ? Number(params.id) : null;
  const valid = id !== null && Number.isInteger(id) && id > 0;

  const { data, isPending, isError, error } = useLoad(valid ? id : null);
  const patch = usePatchLoad(id ?? 0);

  const [amount, setAmount] = useState("");
  const [touched, setTouched] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [amountBaseline, setAmountBaseline] = useState("");
  const initedFor = useRef<number | null>(null);

  useEffect(() => {
    const load = data?.load;
    if (load && initedFor.current !== load.id) {
      const init = buildFieldState(load);
      setFields(init);
      setBaseline(init);
      setAmount(load.amount ?? "");
      setAmountBaseline(load.amount ?? "");
      initedFor.current = load.id;
    }
  }, [data]);

  const parsedAmount = parseAmount(amount);
  const amountChanged = amount !== amountBaseline;
  const amountInvalid = amountChanged && parsedAmount === null;
  const fieldPatch = collectFieldPatch(fields, baseline);
  const dirty = amountChanged || Object.keys(fieldPatch).length > 0;

  function onSave() {
    if (id == null || !dirty || amountInvalid) {
      if (amountInvalid) setTouched(true);
      return;
    }

    const payload: LoadPatchPayload = { ...fieldPatch };
    if (amountChanged && parsedAmount !== null) payload["amount"] = parsedAmount;
    if (Object.keys(payload).length === 0) return;

    patch.mutate(payload, {
      onSuccess: (res) => {
        // Re-baseline off the server's response so the screen reflects
        // what was actually stored, not what we hoped we sent.
        const init = buildFieldState(res.load);
        setFields(init);
        setBaseline(init);
        setAmount(res.load.amount ?? "");
        setAmountBaseline(res.load.amount ?? "");
        toast.success("Load updated");
      },
      // Save failures render inline (see below) rather than as a toast.
    });
  }

  return (
    <div className="min-h-dvh max-w-2xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link href="/">
            <button type="button" aria-label="Back" className="p-2 -ml-2 text-muted-foreground">
              <ArrowLeft className="h-5 w-5" />
            </button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {data?.load.containerNo ?? `Load #${valid ? id : "?"}`}
            </h1>
            {data && (
              <p className="text-sm text-muted-foreground">{formatLoadDate(data.load.loadDate)}</p>
            )}
          </div>
        </div>
        <Button type="button" onClick={onSave} disabled={!dirty || amountInvalid || patch.isPending}>
          {patch.isPending ? "Saving…" : "Save"}
        </Button>
      </header>

      {!valid && <Card className="p-4 text-sm text-destructive">Invalid load id.</Card>}

      {valid && isPending && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {valid && isError && (
        <Card className="p-4 text-sm text-destructive" role="alert">
          Couldn&apos;t open this load: {describeError(error)}
        </Card>
      )}

      {valid && data && (
        <>
          {patch.isError && (
            <Card className="p-4 text-sm text-destructive" role="alert">
              Couldn&apos;t save load: {describeError(patch.error)}
            </Card>
          )}

          <Card className="p-4">
            <AmountField
              value={amount}
              onChange={(v) => setAmount(v)}
              onBlur={() => setTouched(true)}
              showError={touched && amountInvalid}
            />
          </Card>

          <Card className="p-4 space-y-4">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              From the ticket
            </h2>
            <LoadFields
              load={data.load}
              values={fields}
              baseline={baseline}
              onChange={(name, v) => setFields((s) => ({ ...s, [name]: v }))}
            />
          </Card>

          {/* The captured ticket, served by GET /loads/:id/file. Only
              rendered when the load actually has stored bytes — the
              route 404s otherwise and a broken image icon is worse than
              no image. */}
          {data.load.sourceImageUrl && (
            <Card className="overflow-hidden">
              <img
                src={loadFileUrl(data.load.id)}
                alt={`Ticket for load ${data.load.id}`}
                className="w-full h-auto bg-muted"
                loading="lazy"
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
